/* mkv-patch.js — Langue et drapeaux des pistes d'un Matroska, réécrits sans
 * déplacer un octet : l'élément Tracks est reconstruit puis rebouché par un
 * Void, de sorte que la zone garde sa longueur exacte. Le CRC-32, quand il y
 * en a un, est recalculé — après vérification sur le fichier intact.
 *
 * Même forme que patch.js : scan() situe, plan() traduit, parts() décrit. */
'use strict';

import A from './actions.js';
import PATCH from './patch.js';

var CONTAINERS = { 'Matroska': 1, 'WebM': 1 }; // model.container.formatRaw

// Identifiants EBML, marqueur compris.
var ID = {
  segment: 0x18538067, tracks: 0x1654AE6B, entry: 0xAE, cluster: 0x1F43B675,
  trackType: 0x83, language: 0x22B59C, languageBcp47: 0x22B59D,
  flagDefault: 0x88, flagForced: 0x55AA, empty: 0xEC, crc32: 0xBF
};
var KINDS = { 1: 'video', 2: 'audio', 16: 'image', 17: 'text', 18: 'menu' };
var FLAG_ID = { default: ID.flagDefault, forced: ID.flagForced };

var HEAD = 4 * 1024 * 1024;

var LABELS = {
  language: 'Langue', default: 'Par défaut', forced: 'Forcés',
  yes: 'oui', no: 'non', absent: 'absent'
};

var REASONS = {
  container: 'La correction dans le navigateur ne connaît ici que les fichiers Matroska (MKV) et WebM.',
  noTracks: 'La table des pistes est introuvable dans l’en-tête de ce fichier.',
  unreadable: 'L’en-tête de ce fichier ne se lit pas comme un Matroska.',
  noRoom: 'Il n’y a pas assez de place libre après la table des pistes pour y écrire la correction sans déplacer le reste du fichier.',
  crc: 'La somme de contrôle de ce fichier ne se recalcule pas comme attendu : par prudence, rien n’est réécrit.',
  noTrack: 'La piste visée n’a pas été retrouvée dans le fichier.',
  type: 'Cette correction ne peut pas se faire dans le navigateur.',
  lang: 'Le code de langue doit avoir trois lettres.'
};

/* ---------- Entiers de taille variable (EBML) ---------- */

// `keepMarker` : garder le bit de longueur, comme le font les identifiants.
function readVint(buf, pos, keepMarker) {
  var first = buf[pos];
  if (first === undefined) return null;
  var len = 1;
  for (var mask = 0x80; mask && !(first & mask); mask >>= 1) len++;
  if (len > 8 || pos + len > buf.length) return null;
  var value = keepMarker ? first : (first & (0xFF >> len));
  var allOnes = !keepMarker && (first & (0xFF >> len)) === (0xFF >> len);
  for (var i = 1; i < len; i++) {
    value = value * 256 + buf[pos + i];
    if (buf[pos + i] !== 0xFF) allOnes = false;
  }
  return { value: value, len: len, unknown: allOnes }; // tout à 1 : longueur inconnue
}

// Taille sur `len` octets, ou le plus court possible.
function writeVint(value, len) {
  if (!len) { len = 1; while (len < 8 && value >= Math.pow(2, 7 * len) - 1) len++; }
  var out = new Uint8Array(len);
  var v = value;
  for (var i = len - 1; i >= 0; i--) { out[i] = v % 256; v = Math.floor(v / 256); }
  out[0] |= 0x80 >> (len - 1);
  return out;
}

function writeId(id) {
  var bytes = [];
  var v = id;
  while (v > 0) { bytes.unshift(v % 256); v = Math.floor(v / 256); }
  return new Uint8Array(bytes);
}

function concat(list) {
  var n = 0, i;
  for (i = 0; i < list.length; i++) n += list[i].length;
  var out = new Uint8Array(n), at = 0;
  for (i = 0; i < list.length; i++) { out.set(list[i], at); at += list[i].length; }
  return out;
}

// Identifiant, taille, contenu. `pad` rallonge le champ de taille.
function element(id, content, pad) {
  var idb = writeId(id);
  var sz = writeVint(content.length);
  if (pad) sz = writeVint(content.length, sz.length + pad);
  return concat([idb, sz, content]);
}

// Void d'exactement `total` octets ; impossible en un seul (id + taille).
function emptyOf(total) {
  if (total === 0) return new Uint8Array(0);
  if (total === 1) return null;
  for (var sizeLen = 1; sizeLen <= 8; sizeLen++) {
    var body = total - 1 - sizeLen;
    if (body < 0) break;
    if (body < Math.pow(2, 7 * sizeLen) - 1) {
      return concat([writeId(ID.empty), writeVint(body, sizeLen), new Uint8Array(body)]);
    }
  }
  return null;
}

/* ---------- Somme de contrôle ---------- */

var CRC_TABLE = null;
function crcTable() {
  if (CRC_TABLE) return CRC_TABLE;
  CRC_TABLE = new Int32Array(256);
  for (var n = 0; n < 256; n++) {
    var c = n;
    for (var k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    CRC_TABLE[n] = c;
  }
  return CRC_TABLE;
}

function crc32(bytes) {
  var t = crcTable(), c = 0xFFFFFFFF;
  for (var i = 0; i < bytes.length; i++) c = t[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
  return (c ^ 0xFFFFFFFF) >>> 0;
}

function le32(v) {
  return new Uint8Array([v & 0xFF, (v >>> 8) & 0xFF, (v >>> 16) & 0xFF, (v >>> 24) & 0xFF]);
}

/* ---------- Parcours ---------- */

function elementAt(buf, pos) {
  var id = readVint(buf, pos, true);
  if (!id) return null;
  var size = readVint(buf, pos + id.len, false);
  if (!size) return null;
  var dataAt = pos + id.len + size.len;
  return {
    id: id.value, at: pos, dataAt: dataAt, size: size.value,
    sizeLen: size.len, unknown: size.unknown, end: dataAt + size.value
  };
}

/* Situe Tracks et la place libre qui le suit. Seule celle d'après compte :
 * l'index du fichier désigne Tracks par son offset. */
function findTracks(buf) {
  var seg = null, pos = 0;
  while (pos < buf.length) {
    var el = elementAt(buf, pos);
    if (!el) return null;
    if (el.id === ID.segment) { seg = el; break; }
    pos = el.end;
  }
  if (!seg) return null;
  var end = seg.unknown ? buf.length : Math.min(buf.length, seg.end);
  pos = seg.dataAt;
  var tracks = null;
  while (pos < end) {
    var child = elementAt(buf, pos);
    if (!child || child.end > end) break;
    if (child.id === ID.tracks) { tracks = child; pos = child.end; break; }
    if (child.id === ID.cluster) return null; // trop tard : les images commencent
    pos = child.end;
  }
  if (!tracks) return null;
  var free = pos;
  while (free < end) {
    var next = elementAt(buf, free);
    if (!next || next.id !== ID.empty || next.end > end) break;
    free = next.end;
  }
  return { tracks: tracks, regionEnd: free };
}

// 'none' : pas de somme ; 'ok' : recalculée juste ; 'bad' : on renonce.
function crcState(buf, master) {
  var first = elementAt(buf, master.dataAt);
  if (!first || first.id !== ID.crc32 || first.size !== 4) return 'none';
  var stored = first.dataAt + 3 < buf.length
    ? (buf[first.dataAt] | (buf[first.dataAt + 1] << 8) | (buf[first.dataAt + 2] << 16) | (buf[first.dataAt + 3] << 24)) >>> 0
    : -1;
  return crc32(buf.subarray(first.end, master.end)) === stored ? 'ok' : 'bad';
}

/* Enfants dans l'ordre, Void et CRC écartés (refaits à l'écriture).
 * `deep` : ouvrir les TrackEntry ; sinon garder les octets tels quels. */
function parseMaster(buf, master, deep, fail) {
  var kids = [], pos = master.dataAt, crc = crcState(buf, master);
  if (crc === 'bad') { fail.reason = REASONS.crc; return null; }
  while (pos < master.end) {
    var k = elementAt(buf, pos);
    if (!k || k.end > master.end) { fail.reason = REASONS.unreadable; return null; }
    if (k.id !== ID.empty && k.id !== ID.crc32) {
      if (deep && k.id === ID.entry) {
        var inner = parseMaster(buf, k, false, fail);
        if (!inner) return null;
        kids.push({ id: k.id, kids: inner.kids, crc: inner.crc });
      } else {
        kids.push({ id: k.id, content: buf.slice(k.dataAt, k.end) });
      }
    }
    pos = k.end;
  }
  return { kids: kids, crc: crc === 'ok' };
}

function writeNode(node, pad) {
  var body;
  if (node.kids) {
    body = concat(node.kids.map(function (k) { return writeNode(k); }));
    // la somme couvre tout ce qui la suit dans son parent
    if (node.crc) body = concat([element(ID.crc32, le32(crc32(body))), body]);
  } else {
    body = node.content;
  }
  return element(node.id, body, pad);
}

/* ---------- Lecture du fichier ---------- */

// Zone corrigeable et pistes qu'on y trouve. read(offset, length) -> Promise.
async function scan(read, size, containerRaw) {
  if (!CONTAINERS[containerRaw]) return { ok: false, reason: REASONS.container };
  var head;
  try { head = await read(0, Math.min(size, HEAD)); }
  catch (_) { return { ok: false, reason: REASONS.unreadable }; }
  var found = findTracks(head);
  if (!found) return { ok: false, reason: REASONS.noTracks };

  var fail = {};
  var tree = parseMaster(head, found.tracks, true, fail);
  if (!tree) return { ok: false, reason: fail.reason || REASONS.unreadable };

  var counts = {}, tracks = [];
  tree.kids.forEach(function (kid, index) {
    if (kid.id !== ID.entry) return;
    var type = kid.kids.filter(function (c) { return c.id === ID.trackType; })[0];
    var kind = type && type.content.length ? KINDS[type.content[type.content.length - 1]] : null;
    if (!kind) return;
    counts[kind] = (counts[kind] || 0) + 1;
    tracks.push({ kind: kind, ordinal: counts[kind], index: index });
  });

  return {
    ok: true,
    at: found.tracks.at,
    regionLength: found.regionEnd - found.tracks.at,
    region: head.slice(found.tracks.at, found.regionEnd),
    tracks: tracks
  };
}

/* ---------- Traduction des actions en retouches ---------- */

function supports(action) {
  return action.type === 'set-language' || action.type === 'set-flag' || action.type === 'single-default';
}

function bcp47Of(code) {
  var found = A.LANGUAGES.filter(function (l) { return l.code === String(code).toLowerCase(); })[0];
  return found ? found.iso1 : null;
}

function ascii(text) {
  var out = new Uint8Array(text.length);
  for (var i = 0; i < text.length; i++) out[i] = text.charCodeAt(i) & 0x7F;
  return out;
}

function textOf(entry, id) {
  var k = entry.kids.filter(function (c) { return c.id === id; })[0];
  if (!k || !k.content.length) return null;
  return String.fromCharCode.apply(null, k.content);
}

// Drapeau absent : FlagDefault vaut vrai, FlagForced vaut faux.
function flagOf(entry, id) {
  var k = entry.kids.filter(function (c) { return c.id === id; })[0];
  if (!k) return id === ID.flagDefault;
  return k.content[k.content.length - 1] !== 0;
}

function yesNo(v) { return v ? LABELS.yes : LABELS.no; }

// Pose un enfant dans un TrackEntry : remplace s'il y est, ajoute sinon.
function setChild(entry, id, content) {
  for (var i = 0; i < entry.kids.length; i++) {
    if (entry.kids[i].id === id) { entry.kids[i] = { id: id, content: content }; return; }
  }
  entry.kids.push({ id: id, content: content });
}

function dropChild(entry, id) {
  entry.kids = entry.kids.filter(function (k) { return k.id !== id; });
}

// -> { patches: [{offset, bytes}], changes, rejected: [{action, reason}] }
function plan(actions, scanned) {
  // aucune retouche : chaque action que l'on aurait su traiter est refusée
  function refuse(reason, only) {
    return { patches: [], changes: [], rejected: (only ? actions.filter(supports) : actions)
      .map(function (a) { return { action: a, reason: reason }; }) };
  }
  if (!scanned || !scanned.ok) return refuse((scanned && scanned.reason) || REASONS.type);
  // on repart des octets d'origine : plan() est rappelé à chaque changement
  var fail = {};
  var root = elementAt(scanned.region, 0);
  var tree = root && parseMaster(scanned.region, root, true, fail);
  if (!tree) return refuse(fail.reason || REASONS.unreadable);
  var node = { id: ID.tracks, kids: tree.kids, crc: tree.crc };

  function entryFor(target) {
    var t = scanned.tracks.filter(function (x) {
      return x.kind === target.kind && x.ordinal === (target.ordinal || 1);
    })[0];
    return t ? node.kids[t.index] : null;
  }

  var rejected = [], changes = [], touched = false;
  actions.forEach(function (a) {
    if (!supports(a)) return rejected.push({ action: a, reason: REASONS.type });

    if (a.type === 'set-language') {
      var code = String(a.params.language || '').toLowerCase();
      if (!/^[a-z]{3}$/.test(code)) return rejected.push({ action: a, reason: REASONS.lang });
      var entry = entryFor(a.target || {});
      if (!entry) return rejected.push({ action: a, reason: REASONS.noTrack });
      changes.push({ label: LABELS.language, before: textOf(entry, ID.language) || LABELS.absent, after: code });
      setChild(entry, ID.language, ascii(code));
      // LanguageBCP47 prime quand il est là : l'accorder, ou le retirer
      var short = bcp47Of(code);
      if (short) setChild(entry, ID.languageBcp47, ascii(short));
      else dropChild(entry, ID.languageBcp47);
      touched = true;
      return;
    }

    if (a.type === 'set-flag') {
      var id = FLAG_ID[a.params.flag];
      var target = entryFor(a.target || {});
      if (!id) return rejected.push({ action: a, reason: REASONS.type });
      if (!target) return rejected.push({ action: a, reason: REASONS.noTrack });
      changes.push({ label: LABELS[a.params.flag] || a.params.flag,
        before: yesNo(flagOf(target, id)), after: yesNo(!!a.params.value) });
      setChild(target, id, new Uint8Array([a.params.value ? 1 : 0]));
      touched = true;
      return;
    }

    var missing = false;
    (a.params.tracks || []).forEach(function (ordinal) {
      var e = entryFor({ kind: 'audio', ordinal: ordinal });
      if (!e) { missing = true; return; }
      var wanted = ordinal === a.params.keep;
      if (flagOf(e, ID.flagDefault) !== wanted) {
        changes.push({ label: LABELS.default + ' — audio ' + ordinal,
          before: yesNo(!wanted), after: yesNo(wanted) });
      }
      setChild(e, ID.flagDefault, new Uint8Array([wanted ? 1 : 0]));
    });
    if (missing) return rejected.push({ action: a, reason: REASONS.noTrack });
    touched = true;
  });

  if (!touched) return { patches: [], changes: [], rejected: rejected };

  var bytes = writeNode(node);
  var room = scanned.regionLength;
  // un octet de reste ne fait pas un Void : on rallonge le champ de taille
  if (room - bytes.length === 1) bytes = writeNode(node, 1);
  var filler = bytes.length <= room ? emptyOf(room - bytes.length) : null;
  if (!filler) return refuse(REASONS.noRoom, true);
  return { patches: [{ offset: scanned.at, bytes: concat([bytes, filler]) }], changes: changes, rejected: rejected };
}

var api = {
  REASONS: REASONS, CONTAINERS: CONTAINERS, LABELS: LABELS, ID: ID,
  scan: scan, plan: plan, parts: PATCH.parts, supports: supports,
  _readVint: readVint, _writeVint: writeVint, _crc32: crc32, _emptyOf: emptyOf, _element: element
};
globalThis.MediaMkvPatch = api;
export default api;
